<script lang="ts">
  import { onMount } from "svelte";
  import {
    altchaChallengeUrl,
    submitForm,
    type FormKind,
  } from "./api";

  // `contact` (default) targets /contact; `shop` targets /shop-interest.
  export let kind: FormKind = "contact";

  let name = "";
  let email = "";
  let subject = "";
  let message = "";
  let hp = ""; // honeypot — kept empty by humans

  let altchaReady = false;
  let submitting = false;
  let done = false;
  let errorMsg: string | null = null;

  let formEl: HTMLFormElement;

  const subjectLabel = kind === "shop" ? "Worum geht es" : "Betreff";
  const messageLabel = kind === "shop" ? "Deine Nachricht" : "Nachricht";
  const submitLabel = kind === "shop" ? "Kaufinteresse senden" : "Senden";

  // Map backend error tokens to German user-facing messages.
  function describeError(token: string | undefined): string {
    switch (token) {
      case "altcha_invalid":
        return "Die Sicherheitsprüfung ist fehlgeschlagen. Bitte erneut versuchen.";
      case "rate_limited":
        return "Zu viele Anfragen. Bitte später erneut versuchen.";
      case "email_invalid":
        return "Bitte eine gültige E-Mail-Adresse angeben.";
      case "name_required":
      case "email_required":
      case "message_required":
        return "Bitte alle Pflichtfelder ausfüllen.";
      case "recipient_not_configured":
      case "smtp_not_configured":
        return "Der Versand ist derzeit nicht verfügbar. Bitte später erneut versuchen.";
      case "network":
        return "Netzwerkfehler. Bitte Verbindung prüfen und erneut versuchen.";
      default:
        return "Senden fehlgeschlagen. Bitte später erneut versuchen.";
    }
  }

  // Read the Altcha widget's hidden input value (the solution payload).
  function readAltcha(): string {
    const input = formEl?.querySelector<HTMLInputElement>(
      'input[name="altcha"]',
    );
    return input?.value ?? "";
  }

  async function handleSubmit() {
    errorMsg = null;
    if (!name.trim() || !email.trim() || !message.trim()) {
      errorMsg = "Bitte alle Pflichtfelder ausfüllen.";
      return;
    }
    const altcha = readAltcha();
    if (!altcha) {
      errorMsg =
        "Bitte die Sicherheitsprüfung abschließen (Häkchen setzen).";
      return;
    }
    submitting = true;
    const res = await submitForm(kind, {
      name: name.trim(),
      email: email.trim(),
      subject: subject.trim(),
      message: message.trim(),
      altcha,
      hp,
    });
    submitting = false;
    if (res.ok) {
      done = true;
    } else {
      errorMsg = describeError(res.error);
    }
  }

  onMount(async () => {
    // Register the <altcha-widget> custom element + German translations.
    // Sequential: core must run first to create globalThis.$altcha, which
    // altcha/i18n/de checks before registering German strings. Parallel
    // (Promise.all) races and can skip the registration → English fallback.
    await import("altcha");
    await import("altcha/i18n/de");
    altchaReady = true;
  });
</script>

{#if done}
  <div class="callout" role="status" data-testid="form-success">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
    <p>
      <strong>Danke!</strong> Deine Nachricht wurde gesendet. Wir melden uns
      so bald wie möglich.
    </p>
  </div>
{:else}
  <form
    class="wd-form"
    bind:this={formEl}
    on:submit|preventDefault={handleSubmit}
    novalidate
    data-testid="contact-form"
  >
    {#if errorMsg}
      <p class="wd-form__error" role="alert" data-testid="form-error">
        {errorMsg}
      </p>
    {/if}

    <label class="wd-field">
      <span>Name</span>
      <input type="text" bind:value={name} maxlength="200" required autocomplete="name" />
    </label>

    <label class="wd-field">
      <span>E-Mail</span>
      <input type="email" bind:value={email} maxlength="320" required autocomplete="email" />
    </label>

    <label class="wd-field">
      <span>{subjectLabel}</span>
      <input type="text" bind:value={subject} maxlength="300" />
    </label>

    <label class="wd-field">
      <span>{messageLabel}</span>
      <textarea bind:value={message} rows="6" maxlength="5000" required></textarea>
    </label>

    <!-- Honeypot: visually hidden + off-screen; bots fill it, humans don't. -->
    <div class="wd-hp" aria-hidden="true">
      <label>
        Bitte dieses Feld leer lassen
        <input
          type="text"
          bind:value={hp}
          tabindex="-1"
          autocomplete="off"
        />
      </label>
    </div>

    <div class="wd-altcha">
      {#if altchaReady}
        <!-- svelte-ignore a11y-unknown-element -->
        <altcha-widget challenge={altchaChallengeUrl()} language="de"></altcha-widget>
      {:else}
        <p class="wd-form__hint">Sicherheitsprüfung wird geladen…</p>
      {/if}
    </div>

    <button
      class="btn btn--primary"
      type="submit"
      disabled={submitting}
      data-testid="form-submit"
    >
      {submitting ? "Senden…" : submitLabel}
    </button>

    <p class="wd-form__hint">
      Deine Nachricht wird ausschließlich per E-Mail an uns gesendet. Zum
      Schutz vor Spam nutzt das Formular eine selbstgehostete
      Altcha-Sicherheitsprüfung.
    </p>
  </form>
{/if}

<style>
  .wd-form {
    display: grid;
    gap: 16px;
    max-width: 560px;
    margin-top: 18px;
  }
  .wd-field {
    display: grid;
    gap: 6px;
  }
  .wd-field > span {
    font-weight: 600;
    color: var(--ink, inherit);
  }
  .wd-field input,
  .wd-field textarea {
    width: 100%;
    padding: 11px 13px;
    border: 1px solid var(--line, #d4d8de);
    border-radius: 10px;
    background: var(--surface, #fff);
    color: var(--ink, #1a1d22);
    font: inherit;
  }
  .wd-field input:focus,
  .wd-field textarea:focus {
    outline: none;
    border-color: var(--primary, #2f6df6);
    box-shadow: 0 0 0 3px rgba(47, 109, 246, 0.18);
  }
  .wd-altcha {
    margin-block: 4px;
  }
  .wd-form__error {
    color: #b00020;
    background: rgba(176, 0, 32, 0.08);
    border: 1px solid rgba(176, 0, 32, 0.25);
    padding: 10px 12px;
    border-radius: 10px;
  }
  .wd-form__hint {
    color: var(--muted, #5a6270);
    font-size: 14px;
  }
  .btn {
    justify-self: start;
  }
  /* Honeypot: removed from layout + invisible, still focusable by bots only. */
  .wd-hp {
    position: absolute !important;
    left: -9999px !important;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
</style>
